// @ts-check
/**
 * Utilidad centralizada para manejo de fechas en Habitia.
 *
 * El proyecto mezcla 3 formatos incompatibles:
 * 1. Timestamp de Firestore (serverTimestamp(), .toDate())
 * 2. Date de JS
 * 3. Strings ISO "YYYY-MM-DD"
 *
 * Estas funciones normalizan cualquier formato de entrada.
 */
import { Timestamp } from "firebase/firestore";

/**
 * Convierte cualquier formato de fecha a un Date de JS.
 * Retorna null si no se puede parsear.
 */
export function toJSDate(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  // Firestore Timestamp (tiene metodo toDate)
  if (typeof val.toDate === "function") return val.toDate();
  // Objeto con seconds (Timestamp serializado)
  if (val.seconds != null) return new Date(val.seconds * 1000);
  if (typeof val === "string") {
    // "YYYY-MM-DD" se parsea como UTC por new Date(), lo que desplaza
    // la fecha un dia atras en zonas horarias negativas (Uruguay UTC-3).
    // Se agrega T12:00:00 para forzar interpretacion en hora local.
    const s = val.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(val)
      ? val + "T12:00:00"
      : val;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === "number") {
    // Epoch en milisegundos
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Convierte cualquier formato de fecha a un Firestore Timestamp.
 * Retorna null si no se puede parsear.
 */
export function toFirestoreTimestamp(val) {
  if (!val) return null;
  if (val instanceof Timestamp) return val;
  const d = toJSDate(val);
  if (!d) return null;
  return Timestamp.fromDate(d);
}

/**
 * Convierte cualquier formato de fecha a string ISO "YYYY-MM-DD".
 * Util para formularios y inputs type="date".
 * Retorna "" si no se puede parsear.
 */
export function toISODateString(val) {
  const d = toJSDate(val);
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Compara dos fechas (cualquier formato). Retorna:
 * -1 si a < b, 0 si iguales, 1 si a > b, null si alguna es invalida.
 */
export function compareDates(a, b) {
  const da = toJSDate(a);
  const db = toJSDate(b);
  if (!da || !db) return null;
  if (da < db) return -1;
  if (da > db) return 1;
  return 0;
}

/**
 * Verifica si una fecha cae dentro de un mes/anio especifico.
 */
export function isInMonth(val, year, month) {
  const d = toJSDate(val);
  if (!d) return false;
  return d.getFullYear() === year && d.getMonth() + 1 === month;
}
